import { get, patchJSON, postJSON, putJSON } from '../../../utils/Request';

export type PlanPriceSummary = {
  regionCode?: string;
  currency?: string;
  amountMinor?: number;
  currencyFractionDigits?: number;
  durationDays?: number;
  status?: string;
};

export type PlanListItem = {
  planCode?: string;
  displayName?: string;
  grade?: number;
  currentRevisionCode?: string;
  visible?: boolean;
  purchaseEnabled?: boolean;
  prices?: PlanPriceSummary[];
};

/** GET /api/v1/plans 的 content。 */
export type PlanListPage = {
  records?: PlanListItem[];
  total?: number;
  pageNum?: number;
  pageSize?: number;
};

export const PLAN_PRICE_STATUS_LABEL: Record<string, string> = {
  PUBLISHED: '有效',
  DISABLED: '停用'
};

export type PlanOption = {
  planCode?: string;
  displayName?: string;
};

/** 一次返回全部已配置套餐的编码和名称，供筛选下拉框使用。返回体原样交给页面。 */
export function getPlanOptions() {
  return get<PlanOption[]>({
    path: '/api/v1/plans/options'
  });
}

/** 分页查询套餐。返回体原样交给页面。 */
export function getPlans(query: { pageNum: number; pageSize: number }) {
  return get<PlanListPage>({
    path: '/api/v1/plans',
    params: {
      pageNum: query.pageNum,
      pageSize: query.pageSize
    }
  });
}

export type GiftablePlan = {
  planCode?: string;
  displayName?: string;
  grade?: number;
  visible?: boolean;
  purchaseEnabled?: boolean;
};

/** GET /api/v1/gift-orders/plans 的 content。 */
export type GiftablePlanPage = {
  records?: GiftablePlan[];
  total?: number;
  pageNum?: number;
  pageSize?: number;
};

/** 分页查询可赠送套餐。返回体原样交给页面。 */
export function getGiftablePlans(query: { pageNum: number; pageSize: number }) {
  return get<GiftablePlanPage>({
    path: '/api/v1/gift-orders/plans',
    params: {
      pageNum: query.pageNum,
      pageSize: query.pageSize
    }
  });
}

export type GiftOrderListQuery = {
  pageNum: number;
  pageSize: number;
  /** 赠送开始时间，如 2026-10-12T00:00:00+08:00 */
  startTime?: string;
  /** 赠送结束时间，如 2026-10-12T23:59:59+08:00 */
  endTime?: string;
  /** 赠送人名称，模糊匹配；去首尾空格，空白不传 */
  adminName?: string;
  /** 被赠送人名称，模糊匹配；去首尾空格，空白不传 */
  userName?: string;
  /** 赠送套餐编码，21 位数字 */
  planCode?: string;
};

export type GiftOrderListItem = {
  orderNo?: string;
  userCode?: string;
  userName?: string;
  displayName?: string;
  planCode?: string;
  planName?: string;
  adminCode?: string;
  adminName?: string;
  durationDays?: number;
  reason?: string;
  orderStatus?: string;
  fulfillmentStatus?: string;
  giftedAt?: string;
};

function asGiftListRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function parseGiftOrderList(payload: unknown): GiftOrderListItem[] {
  if (Array.isArray(payload)) return payload as GiftOrderListItem[];
  const root = asGiftListRecord(payload);
  if (!root) return [];
  const nested = asGiftListRecord(root.page) ?? asGiftListRecord(root.data) ?? root;
  const list = nested.list ?? nested.records ?? nested.rows ?? nested.items;
  return Array.isArray(list) ? (list as GiftOrderListItem[]) : [];
}

/** 分页查询套餐赠送订单。筛选条件走查询参数，空值不传。返回体原样交给页面。 */
export function queryGiftOrders(query: GiftOrderListQuery) {
  const params: Record<string, string | number> = {
    pageNum: query.pageNum,
    pageSize: query.pageSize
  };
  const startTime = query.startTime?.trim();
  const endTime = query.endTime?.trim();
  const adminName = query.adminName?.trim();
  const userName = query.userName?.trim();
  const planCode = query.planCode?.trim();
  if (startTime) params.startTime = startTime;
  if (endTime) params.endTime = endTime;
  if (adminName) params.adminName = adminName;
  if (userName) params.userName = userName;
  if (planCode) params.planCode = planCode;
  return get<GiftOrderListItem[] | Record<string, unknown>>({
    path: '/api/v1/gift-orders',
    params
  }).then((res) => ({
    ...res,
    list: res.code === 200 ? parseGiftOrderList(res.content ?? res.data) : []
  }));
}

export type PlanDetailPlan = {
  planCode?: string;
  displayName?: string;
  grade?: number;
  currentRevisionCode?: string;
  visible?: boolean;
  purchaseEnabled?: boolean;
};

export type PlanRevision = {
  revisionCode?: string;
  planCode?: string;
  revisionNo?: number;
  displayName?: string;
  shopBaseLimit?: number;
  inviteBaseLimit?: number;
  invitePeriod?: string;
  dmBaseLimit?: number;
  dmPeriod?: string;
  mailBaseLimit?: number;
  mailPeriod?: string;
};

export type PlanFeatureDescription = {
  title?: string;
  displayType?: string;
  supported?: boolean;
  quota?: number;
  unit?: string;
  period?: string;
  text?: string;
};

export type PlanFeaturePolicy = {
  featureNo?: string;
  policyType?: string;
  period?: string;
  quotaLimit?: number;
};

export type PlanSku = {
  skuCode?: string;
  skuType?: string;
  planCode?: string;
  planRevisionCode?: string;
  regionCode?: string;
  currency?: string;
  amountMinor?: number;
  currencyFractionDigits?: number;
  durationDays?: number;
  status?: string;
};

export type PlanRevisionDetail = {
  revision?: PlanRevision;
  featureDescriptions?: PlanFeatureDescription[];
  groupCodes?: string[];
  effectiveFeatureCodes?: string[];
  featurePolicies?: PlanFeaturePolicy[];
  prices?: PlanSku[];
};

export type PlanConfiguration = {
  plan?: PlanDetailPlan;
  currentRevision?: PlanRevisionDetail;
};

export type PlanDetailContent = {
  data?: PlanConfiguration;
  configHash?: string;
};

export const PLAN_PERIOD_LABEL: Record<string, string> = {
  DAY: '日',
  WEEK: '周',
  MONTH: '月',
  TOTAL: '总次数',
  NONE: '持续容量'
};

export const PLAN_SKU_TYPE_LABEL: Record<string, string> = {
  PLAN: '套餐规格',
  ENTITLEMENT: '独立权益包'
};

/** 查询完整套餐详情。content.configHash 修改展示或购买开关时原样传回。 */
export function getPlanDetail(planCode: string) {
  return get<PlanDetailContent>({
    path: `/api/v1/plans/${encodeURIComponent(planCode)}`
  });
}

export type PlanAvailabilityBody = {
  visible: boolean;
  purchaseEnabled: boolean;
  expectedConfigHash: string;
};

/** 设置套餐展示与购买。expectedConfigHash 原样传回详情里的 configHash。配置变化时返回 409。 */
export function updatePlanAvailability(planCode: string, body: PlanAvailabilityBody) {
  return patchJSON<PlanDetailContent>({
    path: `/api/v1/plans/${encodeURIComponent(planCode)}/availability`,
    params: {
      visible: body.visible,
      purchaseEnabled: body.purchaseEnabled,
      expectedConfigHash: body.expectedConfigHash
    }
  });
}

export type CreatePlanFeatureDescription = {
  title?: string;
  displayType?: string;
  supported?: boolean;
  quota?: number;
  unit?: string;
  period?: string;
  text?: string;
};

/** 所选权限组展开后的 API 权限额度策略，按账号共享。 */
export type CreatePlanFeaturePolicy = {
  featureNo: string;
  period: string;
  quotaLimit?: number;
};

export type CreatePlanPrice = {
  regionCode: string;
  currency: string;
  amountMinor?: number;
  durationDays: number;
};

export type CreatePlanBody = {
  displayName: string;
  grade: number;
  featureDescriptions: CreatePlanFeatureDescription[];
  revision: {
    shopBaseLimit: number;
    inviteBaseLimit: number;
    invitePeriod: string;
    dmBaseLimit: number;
    dmPeriod: string;
    mailBaseLimit: number;
    mailPeriod: string;
    groupCodes: string[];
    featurePolicies: CreatePlanFeaturePolicy[];
  };
  prices: CreatePlanPrice[];
};

/** 新增完整套餐。编码和规格由后端生成，返回体原样交给页面。 */
export function createPlan(body: CreatePlanBody) {
  return postJSON<PlanDetailContent>({
    path: '/api/v1/plans',
    params: body
  });
}

export type UpdatePlanBody = {
  displayName: string;
  featureDescriptions: CreatePlanFeatureDescription[];
  revision: {
    shopBaseLimit: number;
    inviteBaseLimit: number;
    invitePeriod: string;
    dmBaseLimit: number;
    dmPeriod: string;
    mailBaseLimit: number;
    mailPeriod: string;
    groupCodes: string[];
    featurePolicies: CreatePlanFeaturePolicy[];
  };
  prices: CreatePlanPrice[];
  expectedConfigHash: string;
};

/** 编辑完整套餐。expectedConfigHash 原样传回详情里的 configHash。配置变化时返回 409。 */
export function updatePlan(planCode: string, body: UpdatePlanBody) {
  return putJSON<PlanDetailContent>({
    path: `/api/v1/plans/${encodeURIComponent(planCode)}`,
    params: body
  });
}

export type PackageCode = 'trial' | 'pro' | 'luxury';
export type GiftStatus = 'active' | 'expired' | 'revoked';

export type PackagePriceItem = {
  price: number;
  currency: string;
  validityDays: number;
  originalPrice: number;
  region: string;
};

export type AdminPackage = {
  id: string;
  code: string;
  name: string;
  level: number;
  version: string;
  prices: PackagePriceItem[];
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
};

export type GiftRecord = {
  id: string;
  orgName: string;
  packageName: string;
  packageCode: PackageCode;
  giftedBy: string;
  giftedAt: string;
  remark: string;
  status: GiftStatus;
};

export const GIFT_STATUS_LABEL: Record<GiftStatus, string> = {
  active: '已生效',
  expired: '已过期',
  revoked: '已撤销'
};

export const ENABLE_STATUS_OPTIONS = [
  { value: 'enabled', label: '正常销售' },
  { value: 'disabled', label: '已下架' }
] as const;

export const adminPackageList: AdminPackage[] = [
  {
    id: 'pkg-1',
    code: '2020755695030284288',
    name: '基础会员',
    level: 0,
    version: '1.0',
    prices: [
      {
        price: 0,
        currency: 'CNY',
        validityDays: 14,
        originalPrice: 0,
        region: '中国大陆'
      },
      {
        price: 0,
        currency: 'VND',
        validityDays: 14,
        originalPrice: 0,
        region: '越南'
      }
    ],
    enabled: false,
    createdAt: '2026-03-02T17:46:47',
    updatedAt: '2026-09-14T12:02:46'
  },
  {
    id: 'pkg-2',
    code: '2020755695030284291',
    name: '专业版',
    level: 1,
    version: '1.0',
    prices: [
      {
        price: 1999,
        currency: 'CNY',
        validityDays: 30,
        originalPrice: 2499,
        region: '中国大陆'
      },
      {
        price: 3599,
        currency: 'CNY',
        validityDays: 60,
        originalPrice: 4499,
        region: '中国大陆'
      },
      {
        price: 386100,
        currency: 'VND',
        validityDays: 30,
        originalPrice: 480000,
        region: '越南'
      },
      {
        price: 690000,
        currency: 'VND',
        validityDays: 60,
        originalPrice: 860000,
        region: '越南'
      },
      {
        price: 9900,
        currency: 'THB',
        validityDays: 30,
        originalPrice: 12000,
        region: '泰国'
      }
    ],
    enabled: true,
    createdAt: '2026-03-02T17:48:12',
    updatedAt: '2026-10-01T09:15:30'
  },
  {
    id: 'pkg-3',
    code: '2020755695030284295',
    name: '豪华版',
    level: 2,
    version: '1.0',
    prices: [
      {
        price: 4999,
        currency: 'CNY',
        validityDays: 30,
        originalPrice: 5999,
        region: '中国大陆'
      },
      {
        price: 8999,
        currency: 'CNY',
        validityDays: 60,
        originalPrice: 10999,
        region: '中国大陆'
      },
      {
        price: 980000,
        currency: 'VND',
        validityDays: 30,
        originalPrice: 1200000,
        region: '越南'
      },
      {
        price: 1760000,
        currency: 'VND',
        validityDays: 60,
        originalPrice: 2100000,
        region: '越南'
      },
      {
        price: 24900,
        currency: 'THB',
        validityDays: 30,
        originalPrice: 29900,
        region: '泰国'
      }
    ],
    enabled: true,
    createdAt: '2026-03-02T17:50:03',
    updatedAt: '2026-10-08T16:40:11'
  }
];

export const giftRecordList: GiftRecord[] = [
  {
    id: 'gift-1',
    orgName: 'VN-BeautyHub',
    packageName: '专业版',
    packageCode: 'pro',
    giftedBy: '运营管理员',
    giftedAt: '2026-10-08 14:22',
    remark: '活动赠送 · 30 天',
    status: 'active'
  }
];
